/* global GMAPS_API_KEY, google */
/*
 * PRD-037 — Seccion de direccion de "Configurar comercio" (app SEPARADA, V4 inline).
 *
 * Replica funcionalmente al AddressForm del panel (que esta acoplado a Redux + GoogleMapsContext +
 * @plick/ui), reusando: (a) el mismo loader de Google Places que el resto del portal
 * (useJsApiLoader + GMAPS_API_KEY) y (b) el mismo mapeo de address_components → campos
 * estructurados. Persiste sobre `commerce.address` (con lat/lng) + `direccion`, que son los campos
 * que el SETUP del dueno (`/configuracion-inicial`) espera ya pre-llenados.
 */
import { type CSSProperties } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import type { Libraries } from '@interfaces/Map';
import type { Commerce } from '@interfaces/general';
import { useDesktop } from '@utils/hooks/useDesktop';
import { CommerceAddressMap } from '@components/maps/CommerceAddressMap';
import { mapPlaceToAddress } from '@utils/address/mapPlaceToAddress';
import { C } from './ui';

const LIBRARIES: Libraries = ['places'];
const FONT = 'Poppins, sans-serif';

// Fallback cuando el comercio aun no tiene coordenadas: centro de Monterrey, NL.
const FALLBACK_CENTER = { lat: 25.6866, lng: -100.3161 };

type CommerceAddress = NonNullable<Commerce['address']>;

const EMPTY_ADDRESS: CommerceAddress = {
  country: 'Mexico',
  state: '',
  city: '',
  neighborhood: '',
  postalCode: '',
  street: '',
  exteriorNumber: '',
  interiorNumber: '',
  fullAddress: '',
  latitude: 0,
  longitude: 0,
};

interface Props {
  commerce: Commerce;
  handleUpdateValue: <K extends keyof Commerce>(key: K, value: Commerce[K]) => void;
}

const inputStyle: CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  fontSize: 14,
  fontFamily: FONT,
  color: C.primary,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 6,
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: C.primary,
  marginBottom: 6,
};

// Reconstruye fullAddress a partir de los campos estructurados (mismo formato que el panel).
const buildFullAddress = (a: CommerceAddress): string =>
  `${a.street} ${a.exteriorNumber}${a.interiorNumber ? ` Int. ${a.interiorNumber}` : ''}, ${a.neighborhood}, ${
    a.postalCode
  }, ${a.city}, ${a.state}, ${a.country}`;

export const ConfigureAddressSection = ({ commerce, handleUpdateValue }: Props) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GMAPS_API_KEY, libraries: LIBRARIES });
  const isDesktop = useDesktop();

  const addr: CommerceAddress = { ...EMPTY_ADDRESS, ...(commerce.address || {}) };
  const hasCoords = addr.latitude !== 0 && addr.longitude !== 0;
  const mapCenter = hasCoords ? { lat: addr.latitude, lng: addr.longitude } : FALLBACK_CENTER;

  // Arrastrar el marcador solo refina el punto lat/lng; conserva los campos estructurados ya capturados.
  const handleMarkerDragEnd = (lat: number, lng: number) => {
    handleUpdateValue('address', { ...addr, latitude: lat, longitude: lng });
  };

  // Elegir una sugerencia llena los campos estructurados + lat/lng (mapeo canónico del panel).
  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    const next = mapPlaceToAddress(place);
    handleUpdateValue('address', next);
    handleUpdateValue('direccion', next.fullAddress || commerce.direccion || '');
  };

  // Edicion manual de un campo: recalcula fullAddress + direccion (conserva lat/lng capturadas).
  const updateField = (key: keyof CommerceAddress, value: string) => {
    const next: CommerceAddress = { ...addr, [key]: value };
    const fullAddress = buildFullAddress(next);
    handleUpdateValue('address', { ...next, fullAddress });
    handleUpdateValue('direccion', fullAddress);
  };

  // Funcion (no componente) que retorna JSX: evita el remount/perdida de foco que provocaria un
  // componente declarado dentro del render (identidad nueva en cada re-render por keystroke).
  const renderField = (label: string, field: keyof CommerceAddress, placeholder?: string, width?: string | number) => (
    <label style={{ display: 'block', marginBottom: 14, width }}>
      <span style={labelStyle}>{label}</span>
      <input
        style={inputStyle}
        placeholder={placeholder}
        value={(addr[field] as string) || ''}
        onChange={(e) => updateField(field, e.target.value)}
      />
    </label>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px' }}>
        Pre-llena la direccion del negocio. El dueno solo la confirmara en su configuracion inicial.
      </p>

      <div style={{ marginBottom: 14 }}>
        <span style={labelStyle}>Buscar direccion</span>
        <CommerceAddressMap
          isLoaded={isLoaded}
          center={mapCenter}
          hasSavedLocation={hasCoords}
          markerPosition={mapCenter}
          onPlaceSelected={handlePlaceSelected}
          onMarkerDragEnd={handleMarkerDragEnd}
          searchPlaceholder="Escribe y elige la direccion del negocio"
          height={isDesktop ? 280 : 200}
        />
        <span style={{ display: 'block', fontSize: 11, color: C.muted, marginTop: 6 }}>
          Arrastra el marcador para fijar el punto exacto del negocio.
        </span>
        {hasCoords && (
          <span style={{ display: 'block', fontSize: 11, color: C.success, marginTop: 6 }}>Ubicacion capturada ✓</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {renderField('Codigo postal', 'postalCode', '12345', 120)}
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Estado', 'state', 'Nuevo Leon')}</div>
      </div>
      {renderField('Ciudad', 'city', 'Monterrey')}
      {renderField('Colonia', 'neighborhood', 'Centro')}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Numero exterior', 'exteriorNumber', '110')}</div>
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Numero interior', 'interiorNumber', 'B (opcional)')}</div>
      </div>
      {renderField('Calle', 'street', 'Calle Principal')}
    </div>
  );
};

export default ConfigureAddressSection;
